import React, { useState, useEffect } from "react";
import { Container, Button } from "react-bootstrap";
import { ApiGet, ApiPost, ApiPut, ApiDel } from "../../../service/ApiService";
import UserModal from "./UserModal"; // Existing modal for add/edit user
import UserRoleModal from "./UserRoleModal"; // Modal for role management
import UsersTable from "./UsersTable"; // Extracted table component

const Users = () => {
    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [roleUser, setRoleUser] = useState(null);
    const [formData, setFormData] = useState({ name: "", tenantId: "" });
    const [error, setError] = useState(null);
    const [rolesRefresh, setRolesRefresh] = useState(0); // New state to force re-fetching roles

    const fetchTenants = React.useCallback(() => {
        setError(null);
        ApiGet(
            "/api/tenant/list",
            (data) => setTenants(data),
            (error) => setError(error)
        );
    }, []);

    const fetchUsers = React.useCallback(() => {
        setError(null);
        ApiGet(
            "/api/user/list",
            (data) => setUsers(data),
            (error) => setError(error)
        );
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSaveUser = () => {
        setShowModal(false);
        setError(null);
        if (currentUser) {
            // Edit user
            ApiPut(
                `/api/user/${currentUser.id}`,
                formData,
                () => {
                    fetchUsers();
                    setShowModal(false);
                    setFormData({ name: "", tenantId: "" });
                    setCurrentUser(null);
                },
                (error) => setError(error)
            );
        } else {
            // Add user
            ApiPost(
                "/api/user/add",
                formData,
                () => {
                    fetchUsers();
                    setShowModal(false);
                    setFormData({ name: "", tenantId: "" });
                },
                (error) => setError(error)
            );
        }
    };

    const handleDeleteUser = (id) => {
        setError(null);
        const confirmDelete = window.confirm("Are you sure you want to delete this user?");
        if (confirmDelete) {
            ApiDel(
                `/api/user/${id}`,
                () => fetchUsers(),
                (error) => setError(error)
            );
        }
    };

    const openModal = (user = null) => {
        setCurrentUser(user);
        const initialFormData = user
            ? { email: user.email, tenantId: user.tenant.id }
            : { email: "", tenantId: 0 };
        setFormData(initialFormData);
        setShowModal(true);
    };

    const openRoleModal = (user) => {
        setRoleUser(user);
        setShowRoleModal(true);
    };

    const closeRoleModal = () => {
        setShowRoleModal(false);
        setRoleUser(null);
    };

    const resetPassword = (user) => {
        setError(null);
        const baseUrl = window.location.origin + "/dmz";
        ApiPost(
            `/api/user/generate-set-url`,
            { email: user.email, baseUrl: baseUrl },
            (data) => {
                alert("Password reset successfully\n\n" + data);
            },
            (error) => setError(error)
        );
    };

    useEffect(() => {
        fetchUsers();
        fetchTenants();
    }, [fetchUsers, fetchTenants]);

    return (
        <Container fluid className="mt-5">
            <h2 className="mb-2">Users Management</h2>
            {error && <div className="alert alert-danger">Error: {error.message}</div>}
            <div className="d-flex justify-content-between mb-3">
                <div></div>
                <Button
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={() => openModal()}
                >
                    Add User
                </Button>
            </div>
            <UsersTable 
                tenants={tenants} 
                users={users} 
                rolesRefresh={rolesRefresh}
                onEdit={openModal} 
                onResetPassword={resetPassword} 
                onManageRoles={openRoleModal} 
                onDelete={handleDeleteUser} 
            />
            <UserModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSave={handleSaveUser}
                formData={formData}
                handleInputChange={handleInputChange}
                currentUser={currentUser}
                tenants={tenants}
            />
            {showRoleModal && roleUser && (
                <UserRoleModal
                    show={showRoleModal}
                    onHide={closeRoleModal}
                    user={roleUser}
                    refreshUsers={fetchUsers}
                    onRoleUpdate={() => setRolesRefresh(prev => prev + 1)}
                />
            )}
        </Container>
    );
};

export default Users;
